import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react'
import moment from 'moment'
import Wrapper from '../../components/Wrapper'
import { Helmet } from 'react-helmet'
import { Box, Paper, Typography, CircularProgress, Accordion, AccordionSummary, IconButton } from '@mui/material'
import { useNavigate, useParams } from 'react-router-dom'
import { useSelector } from 'react-redux'
import {
  ArcElement,
  BarElement,
  CategoryScale,
  Chart as ChartJS,
  Filler,
  Legend,
  LinearScale,
  LineElement,
  PointElement,
  Tooltip,
} from 'chart.js'
import { Link, useLocation } from 'react-router-dom'
import TelegramIcon from '@mui/icons-material/Telegram'
import WhatsAppIcon from '@mui/icons-material/WhatsApp'
import PrintIcon from '@mui/icons-material/Print'
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf'
import html2canvas from 'html2canvas'
import { jsPDF } from 'jspdf'
import Api from '../../Api'
import { menu, shortFunction, operatorReport as opTr, graphics, telecomunication } from '../../translate'
import { operatorReportStyles as opStyles, historyChatsStyles, reportPrimary, reportHover } from '../../helpers/style'
import {
  PERIODS,
  CALL_TYPES,
  CALL_DIRECTIONS,
  VIEW_OVERVIEW,
  VIEW_CALL_COUNT,
  VIEW_DIRECTIONS,
  VIEW_NUMBERS,
  normalizeDisposition,
  normalizeDirection,
  getRandomDemoCalls,
} from '../../helpers/demo'
import { toast } from 'react-toastify'
import ReportHeader from './OperatorReport/ReportHeader'
import ReportTabs from './OperatorReport/ReportTabs'
import ReportPeriodRow from './OperatorReport/ReportPeriodRow'
import OverviewPage from './OperatorReport/OverviewPage'
import CallCountPage from './OperatorReport/CallCountPage'
import DirectionsPage from './OperatorReport/DirectionsPage'
import NumbersPage from './OperatorReport/NumbersPage'
import qs from 'query-string'
import { hasHistoryView } from '../../helpers/employeePermissions'
ChartJS.register(ArcElement, BarElement, CategoryScale, Filler, LinearScale, LineElement, PointElement, Tooltip, Legend)

const primaryColor = reportPrimary

function OperatorReport() {
  const navigate = useNavigate()
  const { type } = useParams()
  const location = useLocation()
  const query = qs.parse(location.search, { arrayFormat: 'comma' })
  const [expanded, setExpanded] = useState(true)
  const activeView = type || VIEW_OVERVIEW
  const langIndex = +localStorage.getItem('atsLang') || 1
  const userData = useSelector((state) => state.users.userData) || {}
  const employeeData = useSelector((state) => state.users.employeeData)
  const showCallHistoryNav = hasHistoryView(employeeData)
  const [period, setPeriod] = useState('today')
  const [refreshing, setRefreshing] = useState(false)
  const [report, setReport] = useState(() => getRandomDemoCalls())
  const [operatorId, setOperatorId] = useState('')
  const [dateFrom, setDateFrom] = useState(() => {
    const t = new Date().toISOString().slice(0, 10)
    return t
  })
  const [dateTo, setDateTo] = useState(() => new Date().toISOString().slice(0, 10))
  const [employees, setEmployees] = useState([])
  const [selectedEmployee, setSelectedEmployee] = useState(null)
  const [statusDirection, setStatusDirection] = useState('incoming')
  const [hasSelectedPeriod, setHasSelectedPeriod] = useState(false)
  const [historyData, setHistoryData] = useState(null)
  const [historyFacetFields, setHistoryFacetFields] = useState(null)
  const [graphicStatusData, setGraphicStatusData] = useState(null)
  const [graphicDispositionData, setGraphicDispositionData] = useState(null)
  const [historyGraphicLoading, setHistoryGraphicLoading] = useState(false)
  const [historyGraphicError, setHistoryGraphicError] = useState(null)
  const [historyGraphicRefreshKey, setHistoryGraphicRefreshKey] = useState(0)
  const [pdfExporting, setPdfExporting] = useState(false)
  const printRootRef = useRef(null)

  const handleExportPdf = useCallback(async () => {
    const root = printRootRef.current || document.getElementById('operator-report-print-root')
    if (!root) {
      toast.error(opTr?.pdf_export_error?.[langIndex] ?? 'Could not find report to export.')
      return
    }
    setPdfExporting(true)
    try {
      const canvas = await html2canvas(root, {
        scale: 2,
        useCORS: true,
        allowTaint: true,
        logging: false,
        scrollX: 0,
        scrollY: -window.scrollY,
        windowWidth: root.scrollWidth,
        windowHeight: root.scrollHeight,
        backgroundColor: '#ffffff',
      })
      const imgData = canvas.toDataURL('image/jpeg', 0.92)
      const pdf = new jsPDF({ orientation: 'p', unit: 'mm', format: 'a4' })
      const pageWidth = pdf.internal.pageSize.getWidth()
      const pageHeight = pdf.internal.pageSize.getHeight()
      const imgWidth = pageWidth
      const imgHeight = (canvas.height * imgWidth) / canvas.width
      let heightLeft = imgHeight
      let position = 0
      pdf.addImage(imgData, 'JPEG', 0, position, imgWidth, imgHeight)
      heightLeft -= pageHeight
      while (heightLeft > 0) {
        position = heightLeft - imgHeight
        pdf.addPage()
        pdf.addImage(imgData, 'JPEG', 0, position, imgWidth, imgHeight)
        heightLeft -= pageHeight
      }
      const safeView = String(activeView || VIEW_OVERVIEW).replace(/[^a-z0-9_-]/gi, '-')
      pdf.save(`operator-report-${safeView}-${dateFrom}-${dateTo}.pdf`)
      toast.success(opTr?.pdf_export_ok?.[langIndex] ?? 'PDF downloaded.')
    } catch (err) {
      console.error('PDF export failed:', err)
      toast.error(opTr?.pdf_export_error?.[langIndex] ?? 'PDF export failed.')
    } finally {
      setPdfExporting(false)
    }
  }, [activeView, dateFrom, dateTo, langIndex, opTr])

  useEffect(() => {
    const params = new URLSearchParams(window.location.search)
    const fromUrl = params.get('operator')
    const fromUser = userData?.name ?? userData?.id ?? ''
    setOperatorId(fromUrl || fromUser || '')
  }, [userData?.name, userData?.id])

  useEffect(() => {
    let cancelled = false
    Api.employeeGetEmployees()
      .then((res) => {
        if (cancelled) return
        const list = res?.data?.employees || []
        const unique = list.filter((emp, i, self) =>
          i === self.findIndex((e) => (e.buddy && e.buddy === emp.buddy) || (e.id && e.id === emp.id))
        )
        setEmployees(unique)
        if (operatorId && unique.length) {
          const match = unique.find((e) => e.buddy === operatorId || e.name === operatorId || String(e.id) === String(operatorId))
          if (match) setSelectedEmployee(match)
        }
      })
      .catch(() => {})
    return () => { cancelled = true }
  }, [operatorId])

  const handleEmployeeChange = useCallback((emp) => {
    setSelectedEmployee(emp ?? null)
    setOperatorId(emp ? (emp.buddy || emp.name || emp.id) : '')
  }, [])

  const fetchReport = useCallback(() => {
    setRefreshing(true)
    setHistoryGraphicRefreshKey((k) => k + 1)
  }, [])

  useEffect(() => {
    if (hasSelectedPeriod) fetchReport()
  }, [hasSelectedPeriod, fetchReport])

  const handleRefresh = () => {
    setRefreshing(true)
    fetchReport()
  }

  const handlePeriodChange = (newPeriod) => {
    setHasSelectedPeriod(true)
    setPeriod(newPeriod)
    if (newPeriod === 'today') {
      const t = new Date().toISOString().slice(0, 10)
      setDateFrom(t)
      setDateTo(t)
    } else if (newPeriod === 'week') {
      const d = new Date()
      setDateTo(d.toISOString().slice(0, 10))
      d.setDate(d.getDate() - 7)
      setDateFrom(d.toISOString().slice(0, 10))
    } else if (newPeriod === 'month') {
      const d = new Date()
      setDateTo(d.toISOString().slice(0, 10))
      d.setDate(d.getDate() - 30)
      setDateFrom(d.toISOString().slice(0, 10))
    }
  }

  const formatDuration = (seconds) => {
    if (seconds == null || isNaN(seconds)) return '0:00'
    const m = Math.floor(Number(seconds) / 60)
    const s = Math.floor(Number(seconds) % 60)
    return `${m}:${String(s).padStart(2, '0')}`
  }

  const formatDate = (isoOrStamp) => {
    if (!isoOrStamp) return '—'
    const m = typeof isoOrStamp === 'number' ? moment.unix(isoOrStamp) : moment(isoOrStamp)
    if (!m.isValid()) return '—'
    return m.subtract(4, 'hours').format('DD-MM-YYYY HH:mm:ss')
  }

  const getPeriodLabel = (p) => {
    const item = PERIODS.find((x) => x.value === p)
    return item ? opTr[item.labelKey]?.[langIndex] ?? p : p
  }

  const getCallTypeLabel = (key) => {
    const t = CALL_TYPES.find((x) => x.key === key)
    return t ? opTr[t.labelKey]?.[langIndex] ?? key : key
  }

  const getDirectionLabel = (key) => {
    const d = CALL_DIRECTIONS.find((x) => x.key === key)
    return d ? opTr[d.labelKey]?.[langIndex] ?? key : key || '—'
  }

  const getCallTypeStyle = (typeKey) => {
    const t = CALL_TYPES.find((x) => x.key === typeKey)
    if (!t) return { bg: 'rgba(156, 163, 175, 0.2)', color: '#6b7280', border: '#9ca3af' }
    return { bg: `${t.color}26`, color: t.color, border: t.color }
  }

  const pieChartData = useMemo(() => {
    const data = [report.answered || 0, report.answeredLater || 0, report.notAnswered || 0, report.busy || 0, report.failed || 0, report.canceled || 0]
    const labels = CALL_TYPES.map((t) => opTr[t.labelKey]?.[langIndex] ?? t.key)
    const colors = CALL_TYPES.map((t) => t.color)
    return {
      labels,
      datasets: [{ data, backgroundColor: colors.map((c) => c + 'cc'), borderColor: colors, borderWidth: 2 }],
    }
  }, [report.answered, report.answeredLater, report.notAnswered, report.busy, report.failed, report.canceled, langIndex])

  const barChartData = useMemo(() => {
    const byDay = {}
    report.calls.forEach((c) => {
      const d = c.start ? new Date(c.start).toISOString().slice(0, 10) : ''
      if (d) byDay[d] = (byDay[d] || 0) + 1
    })
    const sorted = Object.entries(byDay).sort((a, b) => a[0].localeCompare(b[0]))
    return {
      labels: sorted.map(([d]) => d),
      datasets: [{
        label: opTr.calls?.[langIndex] ?? 'Calls',
        data: sorted.map(([, n]) => n),
        backgroundColor: primaryColor,
        borderColor: primaryColor,
        borderWidth: 1,
      }],
    }
  }, [report.calls, langIndex])

  const workerRating = useMemo(() => {
    const rated = report.calls.filter((c) => c.rate != null && c.rate > 0)
    if (rated.length === 0) return { average: 0, count: 0 }
    const sum = rated.reduce((s, c) => s + (Number(c.rate) || 0), 0)
    return { average: sum / rated.length, count: rated.length }
  }, [report.calls])

  const directionChartData = useMemo(() => {
    const byDir = {}
    report.calls.forEach((c) => {
      const d = normalizeDirection(c)
      byDir[d] = (byDir[d] || 0) + 1
    })
    return {
      labels: CALL_DIRECTIONS.map((d) => opTr[d.labelKey]?.[langIndex] ?? d.key),
      datasets: [{
        label: opTr.calls?.[langIndex] ?? 'Calls',
        data: CALL_DIRECTIONS.map((d) => byDir[d.key] || 0),
        backgroundColor: CALL_DIRECTIONS.map((d) => d.color),
        borderColor: CALL_DIRECTIONS.map((d) => d.color),
        borderWidth: 1,
      }],
    }
  }, [report.calls, langIndex])

  const title = shortFunction.operator_report?.[langIndex] ?? 'Report'

  const setView = (view) => {
    const path = '/operator_report'
    navigate(view ? `${path}/${view}` : path)
  }
  const tabValue = activeView || ''


  useEffect(() => {
    let cancelled = false
    const todayStr = new Date().toISOString().slice(0, 10)
    const startTime = period === 'today'
      ? new Date(todayStr + 'T00:00:00.000Z').toISOString()
      : (dateFrom ? new Date(dateFrom + 'T00:00:00.000Z').toISOString() : '')
    const endTime = period === 'today'
      ? new Date(todayStr + 'T23:59:59.999Z').toISOString()
      : (dateTo ? new Date(dateTo + 'T23:59:59.999Z').toISOString() : '')
    const isTrue = startTime && endTime ? (new Date(endTime) - new Date(startTime)) / (24 * 60 * 60 * 1000) < 31 : true
    const callerValue = selectedEmployee ? String(selectedEmployee.buddy ?? selectedEmployee.name ?? selectedEmployee.id ?? '').trim() : undefined
    const historyFilter = {
      startTime: startTime || undefined,
      endTime: endTime || undefined,
      ...(callerValue ? { caller: callerValue } : {}),
    }
    const graphicFilter = {
      timeStartStart: startTime || undefined,
      timeEndEnd: endTime || undefined,
      ...(callerValue ? { caller: callerValue } : {}),
    }

    const emptyReport = {
      received: 0,
      answered: 0,
      answeredLater: 0,
      notAnswered: 0,
      busy: 0,
      failed: 0,
      canceled: 0,
      missed: 0,
      totalDuration: 0,
      calls: [],
    }

    const fetchHistoryAndGraphic = async () => {
      setHistoryGraphicLoading(true)
      setHistoryGraphicError(null)
      try {
        const [historyRes, graphicStatusRes, graphicDispositionRes] = await Promise.all([
          Api.reportHistory(1, 100, historyFilter),
          Api.reportGraphic('status', isTrue, graphicFilter),
          Api.reportGraphic('disposition', isTrue, graphicFilter),
        ])
        if (cancelled) return
        const data = historyRes?.data
        setHistoryData(data?.history1 ?? null)
        setHistoryFacetFields(
          data?.history1?.facet_counts?.facet_fields ??
          data?.ans?.facet_counts?.facet_fields ??
          null
        )
        setGraphicStatusData(graphicStatusRes?.data ?? null)
        setGraphicDispositionData(graphicDispositionRes?.data ?? null)

        const history1 = data?.history1
        const docs = history1?.response?.docs
        if (docs && Array.isArray(docs)) {
          const calls = docs.map((doc) => {
            const main = doc.items?.find((i) => i.uniqueid === doc.linkedid) || doc.items?.[0]
            if (!main) return null
            return {
              uniqueid: main.uniqueid || doc.linkedid,
              start: main.start,
              duration: main.duration,
              disposition: main.disposition,
              direction: main.status || main.direction || (main.line && main.line !== 'null' ? 'outgoing' : 'incoming'),
              status: main.status || (main.line && main.line !== 'null' ? 'Out Call' : 'Incoming Call'),
              rate: main.rate,
              extension: main.extension,
              destination: main.destination,
              in_num: main.in_num,
              ext_num: main.ext_num,
              isAudio: main.isAudio,
            }
          }).filter(Boolean)
          const byType = { answered: 0, answered_later: 0, not_answered: 0, busy: 0, failed: 0, canceled: 0 }
          calls.forEach((c) => {
            const key = normalizeDisposition(c.disposition)
            if (byType[key] !== undefined) byType[key] += 1
          })
          const totalDuration = history1?.stats?.stats_fields?.duration?.sum ?? calls.reduce((s, c) => s + (Number(c.duration) || 0), 0)
          setReport({
            received: calls.length,
            answered: byType.answered,
            answeredLater: byType.answered_later,
            notAnswered: byType.not_answered,
            busy: byType.busy,
            failed: byType.failed,
            canceled: byType.canceled,
            missed: byType.not_answered + byType.busy + byType.failed + byType.canceled,
            totalDuration: Number(totalDuration) || 0,
            calls: calls.slice(0, 50),
          })
        } else {
          setReport(emptyReport)
        }
      } catch (err) {
        if (!cancelled) {
          setHistoryGraphicError(err?.message || 'Failed to load history and graphic')
          setHistoryData(null)
          setHistoryFacetFields(null)
          setGraphicStatusData(null)
          setGraphicDispositionData(null)
          setReport(emptyReport)
        }
      } finally {
        if (!cancelled) {
          setHistoryGraphicLoading(false)
          setRefreshing(false)
        }
      }
    }

    fetchHistoryAndGraphic()
    return () => { cancelled = true }
  }, [period, dateFrom, dateTo, selectedEmployee, historyGraphicRefreshKey])
  const [telegram, setTelegram] = useState(1)
  useEffect(() => {
    (async () => {
      try {
        if (!localStorage.getItem('access_token_urs')) {
          const { data } = await Api.telegramGet()
          setTelegram(data.data.length > 0 ? data.data[0].id : 1)
        }
      } catch (e) {
      }
    })()
  }, [])

  useEffect(() => {
    if (historyGraphicError) toast.error(historyGraphicError)
  }, [historyGraphicError])

  const pathname = location.pathname || ''
  const isHistoryActive = pathname === '/history'
  const isOperatorReportActive = pathname === '/operator_report' || pathname.startsWith('/operator_report/')
  const isTelegramActive = pathname.startsWith('/history_telegram')
  const isWhatsappActive = pathname.startsWith('/history_whatsapp')
  const whatsappGreen = '#25D366'

  return (
    <>
      <Helmet>
        <title>{title} - {menu.worker?.[langIndex] || 'Employee'}</title>
      </Helmet>
      <Wrapper>
    
        <div className="tableParent" style={{ padding: 0 }}>
        <Paper sx={{ minWidth: 'fit-content', fontSize: 10, boxShadow: 'none', borderRadius: 0 }}>
          <Accordion
            expanded={expanded}
            sx={{
              borderRadius: '0 !important',
              '&:before': { borderRadius: 0 },
              '&:first-of-type': { borderTopLeftRadius: 0, borderTopRightRadius: 0 },
              '&:last-of-type': { borderBottomLeftRadius: 0, borderBottomRightRadius: 0 },
              '& .MuiAccordionSummary-root': { borderRadius: 0 },
              '& .MuiAccordionDetails-root': { borderRadius: 0 },
            }}
          >
            <AccordionSummary
              sx={{ ...historyChatsStyles.accordionSummary, borderRadius: 0 }}
              aria-expanded="true"
              aria-controls="panel1a-content"
              id="panel1a-header"
            >
              <div className="w-100" style={historyChatsStyles.headerContainer}>
                <div style={historyChatsStyles.headerRow}>
                  {showCallHistoryNav ? (
                  <>
                  <h1 style={historyChatsStyles.headerTitle}>
                    <Link
                      to={`/history?${qs.stringify(query, { arrayFormat: "comma" })}`}
                      style={{ color: isHistoryActive ? primaryColor : undefined, textDecoration: 'none' }}
                    >
                      {menu.history[+localStorage.getItem('atsLang') || 1]}
                    </Link>
                  </h1>
                  <div className="header_border" style={{ height: 40, marginBottom: 15 }} />
                  <h1 style={historyChatsStyles.headerTitle}>
                    <Link
                      to={`/operator_report`}
                      onClick={() => navigate('/operator_report')}
                      style={{ color: isOperatorReportActive ? primaryColor : undefined, textDecoration: 'none' }}
                    >
                      {graphics.graphic[+localStorage.getItem('atsLang') || 1]}
                    </Link>
                  </h1>
                  <div className="header_border" style={{ height: 40, marginBottom: 15 }} />
                  </>
                  ) : null}
                  <div style={{ ...historyChatsStyles.headerRow, gap: 8 }}>
                    <h1 style={historyChatsStyles.headerTitle}>
                      <Link
                        to={`/history_telegram/${telegram}`}
                        style={{ color: isTelegramActive ? primaryColor : undefined, textDecoration: 'none', display: 'flex', alignItems: 'center', gap: 8 }}
                      >
                        <TelegramIcon sx={{ fontSize: 22 }} />
                        Telegram
                      </Link>
                    </h1>
                  </div>

                  <div className="header_border" style={{ height: 40, marginBottom: 15 }} />
                  <div style={{ ...historyChatsStyles.headerRow, gap: 8 }}>
                    <h1 style={historyChatsStyles.headerTitle}>
                      <Link
                        to="/history_whatsapp"
                        style={{ color: isWhatsappActive ? whatsappGreen : undefined, textDecoration: 'none', display: 'flex', alignItems: 'center', gap: 8 }}
                      >
                        <WhatsAppIcon sx={{ fontSize: 22, color: isWhatsappActive ? whatsappGreen : 'inherit' }} />
                        {telecomunication.whatsapp_title?.[langIndex] || 'WhatsApp'}
                      </Link>
                    </h1>
                  </div>
                </div>

                <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                  <IconButton
                    size="small"
                    onClick={() => window.print()}
                    disabled={pdfExporting}
                    sx={{
                      color: primaryColor,
                      '&:hover': { backgroundColor: `${primaryColor}15` },
                    }}
                    aria-label="Print"
                  >
                    <PrintIcon fontSize="small" />
                  </IconButton>
                  <IconButton
                    size="small"
                    onClick={handleExportPdf}
                    disabled={pdfExporting || historyGraphicLoading}
                    sx={{
                      color: primaryColor,
                      '&:hover': { backgroundColor: `${primaryColor}15` },
                    }}
                    aria-label={opTr.export_pdf_aria[langIndex]}
                  >
                    {pdfExporting ? (
                      <CircularProgress size={18} sx={{ color: primaryColor }} aria-hidden />
                    ) : (
                      <PictureAsPdfIcon fontSize="small" />
                    )}
                  </IconButton>
                </span>
              </div>
            </AccordionSummary>
          </Accordion>
        </Paper>
          <Paper
            ref={printRootRef}
            id="operator-report-print-root"
            sx={{
              ...opStyles.paper,
              width: '100%',
            }}
          >
            {/* <ReportHeader title={title} onBack={() => navigate("/")} /> */}

            {(operatorId || true) && (
              <ReportTabs tabValue={tabValue} onTabChange={setView} opTr={opTr} langIndex={langIndex} />
            )}

          

            
            {(operatorId || true) && (
              <>
                <Box
                  sx={{
                    borderBottom: '1px solid',
                    borderColor: 'divider',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
                    bgcolor: 'grey.50',
                    px: 2,
                    py: 2,
                    mb: 2,
                    borderRadius: 1,
                    mx: 1,
                    mt: 1,
                  }}
                >
                  <ReportPeriodRow
                    period={period}
                    onPeriodChange={handlePeriodChange}
                    onRefresh={handleRefresh}
                    loading={historyGraphicLoading}
                    refreshing={refreshing}
                    getPeriodLabel={getPeriodLabel}
                    dateFrom={dateFrom}
                    dateTo={dateTo}
                    onDateFromChange={setDateFrom}
                    onDateToChange={setDateTo}
                    onCustomRangeSelected={() => setHasSelectedPeriod(true)}
                    employees={employees}
                    selectedEmployee={selectedEmployee}
                    onEmployeeChange={handleEmployeeChange}
                    opTr={opTr}
                    langIndex={langIndex}
                    showDirectionToggle={activeView === VIEW_DIRECTIONS}
                    statusDirection={statusDirection}
                    onStatusDirectionChange={setStatusDirection}
                    getDirectionLabel={getDirectionLabel}
                    showEmployeeSelect={activeView !== VIEW_NUMBERS}
                  />
                </Box>

                {historyGraphicLoading ? (
                  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', py: 6, minHeight: 200 }}>
                    <CircularProgress size={48} sx={{ color: primaryColor, mb: 2 }} />
                    <Typography variant="body2" color="text.secondary">
                      {shortFunction.operator_loading?.[langIndex] ?? 'Loading...'}
                    </Typography>
                  </Box>
                ) : (
                (() => {
                    const contentProps = {
                      report,
                      period,
                      dateFrom,
                      dateTo,
                      calls: report.calls,
                      workerRating,
                      pieChartData,
                      barChartData,
                      directionChartData,
                      opTr,
                      langIndex,
                      getCallTypeLabel,
                      getDirectionLabel,
                      getCallTypeStyle,
                      formatDate,
                      formatDuration,
                      statusDirection,
                      historyData,
                      historyFacetFields,
                      graphicStatusData,
                      graphicDispositionData,
                      historyGraphicLoading,
                      historyGraphicError,
                    }
                    if (!type) return <OverviewPage {...contentProps} />
                    if (type === VIEW_CALL_COUNT) return <CallCountPage {...contentProps} />
                    if (type === VIEW_DIRECTIONS) return <DirectionsPage {...contentProps} />
                    if (type === VIEW_NUMBERS) return <NumbersPage {...contentProps} />
                    return null
                  })()
                )}
              </>
            )}
          </Paper>
        </div>
      </Wrapper>
      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        /* Print should reflect exactly the report body you see on screen */
        @media print {
          body * { visibility: hidden !important; }
          #operator-report-print-root, #operator-report-print-root * {
            visibility: visible !important;
          }
          #operator-report-print-root {
            position: absolute;
            left: 0;
            top: 0;
            width: 100%;
            max-width: 100%;
            margin: 0;
            background: #fff;
            box-shadow: none;
          }
        }
        .operator-report-datepicker.operator-report-datepicker--two-months {
          display: grid !important;
          grid-template-columns: 1fr 1fr;
          grid-template-rows: auto auto;
        }
        .operator-report-datepicker.operator-report-datepicker--two-months .react-datepicker__header {
          grid-column: 1 / -1;
        }
        .operator-report-datepicker.operator-report-datepicker--two-months .react-datepicker__month-container {
          float: none !important;
        }
        .operator-report-datepicker .react-datepicker__day--selected,
        .operator-report-datepicker .react-datepicker__day--in-selecting-range,
        .operator-report-datepicker .react-datepicker__day--in-range,
        .operator-report-datepicker .react-datepicker__month-text--selected,
        .operator-report-datepicker .react-datepicker__month-text--in-selecting-range,
        .operator-report-datepicker .react-datepicker__month-text--in-range {
          background-color: ${reportPrimary} !important;
          color: #fff !important;
        }
        .operator-report-datepicker .react-datepicker__day--selected:hover,
        .operator-report-datepicker .react-datepicker__day--in-selecting-range:hover,
        .operator-report-datepicker .react-datepicker__day--in-range:hover,
        .operator-report-datepicker .react-datepicker__month-text--selected:hover,
        .operator-report-datepicker .react-datepicker__month-text--in-selecting-range:hover,
        .operator-report-datepicker .react-datepicker__month-text--in-range:hover {
          background-color: ${reportHover} !important;
        }
        .operator-report-datepicker .react-datepicker__day--keyboard-selected,
        .operator-report-datepicker .react-datepicker__month-text--keyboard-selected {
          background-color: ${reportPrimary}33 !important;
          color: #333 !important;
        }
        .operator-report-datepicker .react-datepicker__day--keyboard-selected:hover,
        .operator-report-datepicker .react-datepicker__month-text--keyboard-selected:hover {
          background-color: ${reportPrimary} !important;
          color: #fff !important;
        }
        .operator-report-datepicker .react-datepicker__day--in-selecting-range:not(.react-datepicker__day--in-range),
        .operator-report-datepicker .react-datepicker__month-text--in-selecting-range:not(.react-datepicker__month-text--in-range) {
          background-color: ${reportPrimary}80 !important;
        }
      `}</style>
    </>
  )
}

export default OperatorReport
